import React, { useState } from "react";

const TYPE_OPTIONS = ["To Do", "Someday", "Reminder"];
const TAG_OPTIONS = ["Email", "Call", "Pay", "Schedule", "Sign Up"];
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];
const PERSON_OPTIONS = ["Adam", "Kimberly", "Family", "Carissa", "Robert"];

export default function AddTaskSheet({ onClose, onAdd, defaultPerson = "", lockPerson = false, defaultType = "To Do" }) {
  const [form, setForm] = useState({ name: "", dueDate: "", priority: "Medium", type: defaultType, tag: [], person: defaultPerson });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleTag = (t) => {
    setForm(prev => {
      const has = prev.tag.includes(t);
      return { ...prev, tag: has ? prev.tag.filter(x => x !== t) : [...prev.tag, t] };
    });
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    setSaving(true);
    setError("");
    try {
      const res = await fetch("/.netlify/functions/notion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "createTask", value: { ...form, name: form.name.trim() } }),
      });
      const data = await res.json();
      if (data.item) { onAdd(data.item); onClose(); }
      else setError("Failed to create task");
    } catch { setError("Something went wrong"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200 }} />
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderRadius: "20px 20px 0 0", zIndex: 201, paddingBottom: 24, animation: "slideUp 0.25s ease" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)" }}>
          <button onClick={onClose} style={btn}>Cancel</button>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)" }}>New Task</span>
          <button onClick={handleCreate} disabled={saving} style={{ ...btn, color: saving ? "var(--muted)" : "var(--sage)", fontWeight: 800 }}>{saving ? "Creating…" : "Create"}</button>
        </div>
        <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {error && <div style={{ padding: "8px 20px", color: "var(--rose)", fontSize: 13, fontWeight: 600 }}>{error}</div>}

          <div style={field}>
            <div style={label}>Name *</div>
            <input
              autoFocus
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Task name…"
              style={{ width: "100%", border: "none", outline: "none", fontSize: 16, fontWeight: 600, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" }}
            />
          </div>

          <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={label}>Due Date</div>
            <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })}
              style={{ border: "none", outline: "none", fontSize: 14, fontWeight: 600, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" }} />
          </div>

          <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={label}>Priority</div>
            <div style={{ display: "flex", gap: 6 }}>
              {PRIORITY_OPTIONS.map(p => (
                <button key={p} onClick={() => setForm({ ...form, priority: p })} style={{
                  padding: "4px 12px", borderRadius: 20, border: "1.5px solid",
                  borderColor: form.priority === p ? "var(--sage)" : "var(--border)",
                  background: form.priority === p ? "var(--sage)" : "transparent",
                  color: form.priority === p ? "white" : "var(--muted)",
                  fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>{p}</button>
              ))}
            </div>
          </div>

          <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={label}>Type</div>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={sel}>
              {TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          <div style={field}>
            <div style={label}>Tag</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {TAG_OPTIONS.map(t => {
                const checked = form.tag.includes(t);
                return (
                  <button key={t} onClick={() => toggleTag(t)} style={{
                    padding: "4px 12px", borderRadius: 20, border: "1.5px solid",
                    borderColor: checked ? "var(--sage)" : "var(--border)",
                    background: checked ? "var(--sage)" : "transparent",
                    color: checked ? "white" : "var(--muted)",
                    fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}>{t}</button>
                );
              })}
            </div>
          </div>

          <div style={{ ...field, borderBottom: "none", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={label}>Person</div>
            {lockPerson ? (
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--charcoal)" }}>{form.person}</div>
            ) : (
              <select value={form.person} onChange={e => setForm({ ...form, person: e.target.value })} style={sel}>
                <option value="">None</option>
                {PERSON_OPTIONS.map(p => <option key={p}>{p}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateX(-50%) translateY(100%); } to { transform: translateX(-50%) translateY(0); } }`}</style>
    </>
  );
}

const btn = { background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit" };
const field = { padding: "12px 20px", borderBottom: "1px solid var(--border)" };
const label = { fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 };
const sel = { border: "none", outline: "none", fontSize: 14, fontWeight: 600, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent", cursor: "pointer" };
